import React, { useEffect, useState } from "react";
import { Box, Spinner, Text } from "@chakra-ui/react";
import { keyframes } from "@emotion/react";
import { navigate } from "@reach/router";

import logo from "../../../../../icons/logo-text.svg";
import tagline from "../../../../../icons/tagline.svg";
import { authentication } from "../..";
import { refreshLoadedCompanyProfile, refreshLoadedUserProfile, setSwitchFromGuest } from "../../utils";
import { useDispatch, useSelector } from "react-redux";
import { apiV1 } from "api-v2";
import { setCurrentUser } from "../../authSlice";
import { useAppDispatch } from "newui/store/hooks";
import { getUserProfile } from "../../auth-api";

// Slight arm rotation sweep
const armRotate = keyframes`
  0%, 100% { transform: rotate(-10deg); }
`;

// Cable extends and retracts smoothly
const cableLower = keyframes`
  0%, 100% { height: 40px; }
  25%, 75% { height: 120px; }
`;

const fadeInOut = keyframes`
  0%, 100% { opacity: 0.3; }
  50% { opacity: 1; }
`;

const safeUrl = "/account/rfx";

export const CreatingCompanyProfile = () => {
	const switchData = useSelector((state: any) => state.auth.guestSwitch);
	const dispatch = useAppDispatch();

	useEffect(() => {
		if (!switchData.is_loaded) {
			navigate(safeUrl);
			return;
		}

		const runSwitch = async () => {
			try {
				const { user, company, url } = switchData;

				// set new user
				const newUser = await getUserProfile();
				dispatch(setCurrentUser(newUser.data));

				// 5s delay without blocking render
				setTimeout(async () => {
					const navigateTo = url ?? `/account/${company.id}/rfx`;
					navigate(navigateTo);
					dispatch(setSwitchFromGuest(null, null));
				}, 5000);
			} catch (err) {
				console.error(err);
				navigate("/"); // fallback
			}
		};

		runSwitch();
	}, [dispatch, switchData]);

	return (
		<Box
			position="fixed"
			top={0}
			left={0}
			width="100vw"
			height="100vh"
			zIndex={9999} // make sure it overlays everything
			display="flex"
			flexDirection="column"
			alignItems="center"
			justifyContent="center"
			bg="linear-gradient(135deg, #1a365d 0%, rgba(0,69,245,1.0) 100%)"
		>
			<div className="relative mb-[140px] flex items-center">
				{/* Logo with glow */}
				<img
					src={logo}
					alt="Procuracon Logo"
					className="h-auto w-96 drop-shadow-[0_0_10px_white] drop-shadow-[0_0_12px_white] drop-shadow-[0_0_20px_white]"
				/>

				{/* Tagline with smaller glow */}
				<img
					src={tagline}
					alt="Build Possible"
					className="ml-6 h-auto w-40 drop-shadow-[0_0_10px_white] drop-shadow-[0_0_12px_white] drop-shadow-[0_0_20px_white]"
				/>
			</div>
			<Spinner
				size={"xl"}
				color="white"
			/>
			{/* Crane Scene */}
			<Box
				position="relative"
				width="360px"
				height="280px"
				mt={6}
			>
				{/* Tower */}
				<Box
					position="absolute"
					bottom="0"
					left="50%"
					transform="translateX(-50%)"
					width="18px"
					height="180px"
					bg="linear-gradient(180deg, #718096 0%, #4A5568 100%)"
					border="2px solid #2D3748"
					borderRadius="3px"
					boxShadow="inset 0 2px 4px rgba(0,0,0,0.4)"
				>
					{Array.from({ length: 6 }).map((_, i) => (
						<Box
							key={i}
							position="absolute"
							top={`${i * 28}px`}
							left="2px"
							right="2px"
							height="2px"
							bg="#2D3748"
						/>
					))}
				</Box>

				{/* Cabin */}
				<Box
					position="absolute"
					bottom="150px"
					left="50%"
					transform="translateX(10px)"
					width="60px"
					height="40px"
					bg="#ED8936"
					borderRadius="4px"
					boxShadow="0 3px 6px rgba(0,0,0,0.3)"
				>
					<Box
						position="absolute"
						top="10px"
						left="8px"
						width="22px"
						height="12px"
						bg="#2D3748"
						borderRadius="2px"
					/>
				</Box>

				{/* Crane Arm (angled, static) */}
				<Box
					position="absolute"
					bottom="180px"
					left="50%"
					width="220px"
					height="8px"
					transformOrigin="left center"
					animation={`${armRotate} 10s ease-in-out infinite`}
					bg="linear-gradient(90deg, #F6AD55 0%, #ED8936 100%)"
					borderRadius="4px"
					boxShadow="0 2px 4px rgba(0,0,0,0.3)"
				>
					{/* Counterweight */}
					<Box
						position="absolute"
						left="-30px"
						top="-4px"
						width="20px"
						height="16px"
						bg="#4A5568"
						border="2px solid #2D3748"
						borderRadius="2px"
					/>
				</Box>

				{/* Cable (vertical, independent of arm) */}
				<Box
					position="absolute"
					top="68px"
					// bottom="200px" // attach at arm end visually
					left="100%"
					width="2px"
					height="40px"
					bg="#A0AEC0"
					transformOrigin="top center"
					animation={`${cableLower} 10s ease-in-out infinite`}
				>
					{/* Hook */}
					<Box
						position="absolute"
						top="100%"
						left="50%"
						transform="translateX(-50%)"
						width="10px"
						height="6px"
						bg="#2D3748"
						borderRadius="2px"
						boxShadow="0 1px 2px rgba(0,0,0,0.3)"
						backgroundColor="white"
					/>

					{/* Girder */}
					<Box
						position="absolute"
						top="calc(100% + 6px)"
						left="50%"
						transform="translateX(-50%)"
						width="80px"
						height="14px"
						bg="linear-gradient(90deg, #4299E1 0%, #2B6CB0 100%)"
						border="2px solid #2C5282"
						borderRadius="3px"
						boxShadow="0 2px 6px rgba(0,0,0,0.4)"
					>
						<Box
							position="absolute"
							left="6px"
							top="4px"
							width="3px"
							height="6px"
							bg="#2C5282"
						/>
						<Box
							position="absolute"
							right="6px"
							top="4px"
							width="3px"
							height="6px"
							bg="#2C5282"
						/>
					</Box>
				</Box>

				{/* Static girders on ground */}
				{[0, 1, 2].map((i) => (
					<Box
						key={i}
						position="absolute"
						bottom={`${i * 18}px`}
						left="30px"
						width="80px"
						height="14px"
						bg="linear-gradient(90deg, #4299E1 0%, #2B6CB0 100%)"
						border="2px solid #2C5282"
						borderRadius="3px"
						animation={`${fadeInOut} 3s ease-in-out infinite`}
						style={{ animationDelay: `${i * 0.4}s` }}
						boxShadow="0 2px 6px rgba(0,0,0,0.3)"
					/>
				))}
			</Box>

			{/* Text */}
			<Text
				fontSize="2xl"
				fontWeight="600"
				color="white"
				mt={10}
				textAlign="center"
				textShadow="0 2px 4px rgba(0,0,0,0.3)"
			>
				Please wait whilst we setup your company profile...
			</Text>

			<Box
				display="flex"
				gap={2}
				mt={2}
			>
				{[0, 1, 2].map((index) => (
					<Box
						key={index}
						width="10px"
						height="10px"
						borderRadius="50%"
						bg="orange.400"
						animation={`${fadeInOut} 1.5s ease-in-out infinite`}
						style={{ animationDelay: `${index * 0.3}s` }}
					/>
				))}
			</Box>
		</Box>
	);
};
